import React, { useContext, useEffect, useState, useCallback } from "react";
import { message } from "antd";
import MemberProfileContext from "../../reducer";
import { moment } from "../../../../../../../utils";
import { resCatch } from "../../../../../../util/helper";
import { getMoment, getCurrency } from "../../../../../../../utils/helper";
import {
  getConversationNoteApi,
  createConversationNoteApi,
  getOverviewApi,
  getLeadSourceOptionsApi,
  updateLeadSourceApi,
} from "./api";
import CustomerOverview from "./components/CustomerOverview";
import ContactForm from "./components/ContactForm";
import HistoryTable from "./components/HistoryTable";

const PAGE_SIZE = 5;

const initCustomerOverview = {
  first_date: "-",
  last_date: "-",
  total_orders: "-",
  aov: "-",
  last_document_id: null,
  last_document_no: "",
  last_document_report_id: "",
  last_order_items: [],
  ltv: "-",
  lead_source: undefined,
};

const ContactHistory = () => {
  const {
    state: {
      data: { partner_code, ref_type },
    },
    dispatch,
  } = useContext(MemberProfileContext);

  // ─── Section collapse states ─────────────────────────────────────────────
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(true);
  const [isFormExpanded, setIsFormExpanded] = useState(true);

  // ─── Shared data state ───────────────────────────────────────────────────
  const [overview, setOverview] = useState(initCustomerOverview);
  const [historyList, setHistoryList] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  // ─── Fetch History List ──────────────────────────────────────────────────
  const loadHistory = useCallback(
    async (query = "") => {
      if (!partner_code) return;
      setIsLoading(true);
      try {
        const data = await getConversationNoteApi(partner_code, 1, 100, query);
        if (data?.record) setHistoryList(data.record);
      } catch (error) {
        resCatch(error);
      } finally {
        setIsLoading(false);
      }
    },
    [partner_code],
  );

  const handleSearch = useCallback(
    (value) => {
      setSearchQuery(value);
      loadHistory(value);
    },
    [loadHistory],
  );

  // ─── Fetch Lead Source Options ─────────────────────────────────────────
  const loadLeadSourceOptions = useCallback(async () => {
    try {
      const data = await getLeadSourceOptionsApi();
      if (Array.isArray(data)) setLeadSourceOptions(data);
    } catch (error) {
      resCatch(error);
    }
  }, []);

  // ─── Fetch Overview ──────────────────────────────────────────────────────
  const loadOverview = useCallback(async () => {
    if (!partner_code) return;
    try {
      const data = await getOverviewApi(partner_code, ref_type); // partner_code , partner_type
      if (!data?.overview) return;

      const api = data.overview;
      // map API fields → state (ฟิลด์ที่ format ด้วย helper จะอัปเดตได้ที่เดียว)
      setOverview((prev) => ({
        ...prev,
        first_date: getMoment(api.first_date, "DD/MM/YYYY"),
        last_date: getMoment(api.last_date, "DD/MM/YYYY"),
        total_orders: api.total_orders ?? "-",
        aov: getCurrency(api.aov),
        ltv: getCurrency(api.total_price),
        last_document_id: api.last_document_id || null,
        last_document_no: api.last_document_no || "-",
        last_document_report_id: api.last_document_report_id || null,
        last_order_items: data.last_order_items || [],
        lead_source:
          data.lead_source?.value || api.lead_source || prev.lead_source,
      }));
    } catch (error) {
      resCatch(error);
    }
  }, [partner_code]);

  // ─── โหลด data เมื่อ component mount และ partner_code เปลี่ยน ────────────
  useEffect(() => {
    loadLeadSourceOptions();
  }, [loadLeadSourceOptions]);

  useEffect(() => {
    loadHistory();
    loadOverview();
  }, [partner_code, loadHistory, loadOverview]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleOverviewChange = useCallback(
    async (key, value) => {
      setOverview((prev) => ({ ...prev, [key]: value }));

      if (key === "lead_source" && partner_code) {
        try {
          await updateLeadSourceApi(partner_code, ref_type, value); // partner_code , partner_type , lead_source_value
          message.success("อัปเดตช่องทางที่รู้จักสำเร็จ");
        } catch (error) {
          resCatch(error);
        }
      }
    },
    [partner_code, ref_type],
  );

  const handleSaveContact = useCallback(
    async (entry) => {
      try {
        const payload = {
          partner_code,
          description: entry.description || "",
          contact_result: entry.contact_result,
          contact_reason: entry.contact_reason,
          is_purchased: entry.is_purchased,
          is_appointment: entry.is_appointment,
          appointment_date:
            entry.is_appointment && entry.appointment_datetime
              ? moment(entry.appointment_datetime, "DD/MM/YYYY HH:mm").format(
                  "YYYY-MM-DD HH:mm",
                )
              : null,
          appointment_note: entry.appointment_note || "",
        };

        await createConversationNoteApi(payload);
        message.success("บันทึกการติดต่อสำเร็จ");
        loadHistory();
      } catch (error) {
        resCatch(error);
      }
    },
    [partner_code, loadHistory],
  );

  const handleFormDirtyChange = useCallback(
    (isDirty) => {
      dispatch({
        type: "setState",
        payload: { name: "hasUnsavedChanges", value: isDirty },
      });
    },
    [dispatch]
  );

  const toggleOverview = useCallback(
    () => setIsOverviewExpanded((v) => !v),
    [],
  );
  const toggleForm = useCallback(() => setIsFormExpanded((v) => !v), []);

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="contact-history-container" style={{ fontSize: "13px" }}>
      <CustomerOverview
        isExpanded={isOverviewExpanded}
        onToggle={toggleOverview}
        overview={overview}
        onOverviewChange={handleOverviewChange}
        leadSourceOptions={leadSourceOptions}
      />

      <ContactForm
        isExpanded={isFormExpanded}
        onToggle={toggleForm}
        onSave={handleSaveContact}
        onDirtyChange={handleFormDirtyChange}
      />

      <HistoryTable
        historyList={historyList}
        isLoading={isLoading}
        pageSize={PAGE_SIZE}
        onSearch={handleSearch}
      />
    </div>
  );
};

export default ContactHistory;
